'use client'

import { clsx } from 'clsx'
import { type ElementType, type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { useRecommendationIntersection } from '../../../cross-sell'
import { useCartRecommendationWidgetOrderActivation } from '../../../experiments/cart-recommendation-widget-order/useCartRecommendationWidgetOrderActivation'
import {
  EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_PRODUCT_CLICK_MY_ACCOUNT,
  experimentsTrackEvent,
} from '../../../experiments/eventTracking'
import { useSwrFetchRecommendationService } from '../../api'
import {
  gtmDataLayerPushRecommendationTracking,
  RECOMMENDATION_SERVICE_ACCOUNT,
  type RecommendationServiceType,
  type RecommendationWidgetType,
} from '../../config'
import { useGetRecommendationWidgetId } from '../../hooks'
import { RecommendationWidgetContainer } from '../RecommendationAreaContainer'
import { RecommendationWidgetMoreLink } from '../RecommendationAreaMoreLink'
import { RecommendationWidgetProvider } from '../RecommendationAreaProvider'
import { RecommendationWidgetTitle } from '../RecommendationAreaTitle'
import { RecommendationHorizontalSlider } from '../RecommendationHorizontalSlider'

type RecommendationSliderWidgetProps = {
  className?: string
  plist?: string
  serviceId: RecommendationServiceType
  slots?: {
    itemContent?: ElementType
  }
  widgetType: RecommendationWidgetType
}

export const RecommendationSliderWidget: FC<
  RecommendationSliderWidgetProps
> = ({ className, plist = '', serviceId, slots, widgetType }) => {
  const { handleIntersection, intersected } = useRecommendationIntersection()

  const { data, error } = useSwrFetchRecommendationService(
    intersected
      ? {
          pid: '',
          plist,
          sellerid: '',
          serviceId,
        }
      : undefined,
  )
  useCartRecommendationWidgetOrderActivation({
    intersected,
    widgetType,
  })

  const widgetId = useGetRecommendationWidgetId(serviceId, widgetType)
  const recommendation = data?.recommendations

  if (error || !widgetId || recommendation?.[widgetId].content.length === 0) {
    return null
  }

  const widgetData = widgetId ? recommendation?.[widgetId] : undefined

  return (
    <RecommendationWidgetProvider
      serviceId={serviceId}
      widgetData={widgetData}
      widgetId={widgetId}
    >
      <RecommendationWidgetContainer
        className={clsx('max-w-full', className)}
        onIntersect={handleIntersection}
        widgetData={data}
        widgetId={widgetId}
      >
        <div className="flex flex-col justify-between tablet:flex-row tablet:items-center">
          <RecommendationWidgetTitle className="my-2 text-xl tablet:text-2xl" />
          <RecommendationWidgetMoreLink>
            <FormattedMessage id="general.label.ShowAllProducts" />
          </RecommendationWidgetMoreLink>
        </div>
        <RecommendationHorizontalSlider
          onGtmDataLayerPush={(widgetProductId, trackingToken) => {
            gtmDataLayerPushRecommendationTracking({
              trackingToken,
              widgetId,
              widgetProductId,
            })
            if (serviceId === RECOMMENDATION_SERVICE_ACCOUNT) {
              experimentsTrackEvent(
                EXPERIMENTS_TRACK_EVENT_RECOMMENDATION_PRODUCT_CLICK_MY_ACCOUNT,
              )
            }
          }}
          products={widgetData?.content}
          productsPerPage={6}
          slots={slots}
        />
      </RecommendationWidgetContainer>
    </RecommendationWidgetProvider>
  )
}
